























import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import http from '@/services/Http';
import { AutocompleteResult, GeocodeResult } from '@/models/BookMeetingFlow';
import { websiteText } from '@/mixins/websiteTexts';
import GenericSearch from './GenericSearch.vue';

@Component({ components: { GenericSearch } })
class BookMeetingSearch extends Vue {
    @Prop({ default: false }) isInModal!: boolean;
    @Prop() blockPlaceholder!: string;
    @Prop() autoCompleteTopMobile!: boolean;
    @Prop() inMegaMenu!: boolean;
    
    public websiteText = websiteText;
    public searchResults: AutocompleteResult[] = [];
    public isLoading = false;
    public a11yStatusMessage = '';

    // To handle matomo event tracking, we assume there's a global function or mixin that provides this. 
    [x: string]: any; 

    get supportsGeoLocation() {
        return 'geolocation' in navigator;
    }

    public fetchPlaces(query: string) {
        if (!query) {
            this.clearResults();
            return;
        }

        this.isLoading = true;
        this.a11yStatusMessage = this.websiteText('meeting.a11y.searching');

        http.post('/api/retailer/places', { query })
            .then((res: any) => res.json())
            .then((data: AutocompleteResult[]) => {
                this.searchResults = data;
                if (data.length > 0) {
                    const translation = this.websiteText('meeting.a11y.results.available');
                    this.a11yStatusMessage = translation.replace('{0}', data.length.toString());
                } else {
                    const noResultsTranslation = this.websiteText('meeting.a11y.no.results');
                    this.a11yStatusMessage = noResultsTranslation.replace('{0}', query);
                }
            })
            .catch((err: any) => {
                console.error("Error fetching places:", err);
                this.searchResults = [];
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    public fetchCurrentLocation() {
        if (!this.supportsGeoLocation) return;

        this.isLoading = true;

        const success = (position: any) => {
            const { latitude, longitude } = position.coords;
            
            http.post('/api/retailer/places/geocode', { latitude, longitude })
                .then((res: any) => res.json())
                .then((data: GeocodeResult) => {
                    const result: AutocompleteResult = {
                        description: data.placeName,
                        placeId: data.placeId,
                        types: [],
                        latitude,
                        longitude
                    };
                    
                    this.handlePlaceSelection(result);
                })
                .catch((err: any) => {
                    console.error("Error geocoding location:", err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        };

        const error = (err: any) => {
            console.error("Error getting current location:", err);
            this.isLoading = false;
        };

        navigator.geolocation.getCurrentPosition(success, error);
    }

    public handlePlaceSelection(place: AutocompleteResult) {
        if (!place || !place.placeId) return;

        this.$emit('item-selected', place);
        
        this.isLoading = true;

        http.post(`/api/retailer/places/details`, { query: place.placeId })
            .then((res: any) => res.json())
            .then((data: any) => {
                this.clearResults();
                
                if (typeof this.matomoEvent === 'function') {
                    this.matomoEvent("booking_flow", "Search", place.description);
                }

                if (data.latitude && data.longitude) {
                    this.emitLocationChange(data.latitude, data.longitude);
                }
            })
            .catch((err: any) => {
                console.error("Error fetching place details:", err);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    public clearResults() {
        this.searchResults = [];
    }

    @Emit('location-changed')
    private emitLocationChange(lat: number, lng: number) {
        return { lat, lng };
    }
}

export default BookMeetingSearch;
